import React, { useState, useEffect } from "react";

import { ButtonGroup } from "@mui/material";
import { AddCircleOutlineRounded as AddCircleOutlineRoundedIcon } from "@mui/icons-material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { ExternalList } from "../../../../context/MovingResources";
import { Item } from "../../../../models/item";
import { RoomJob } from "../../../../models/sales";
import { ServiceBooking } from "../../../../models/service.booking";
import ItemsService from "../../../../services/items.service";
import MaterialsService from "../../../../services/materials.service";
import RoomService from "../../../../services/room.service";
import { useAppState } from "../../../../context/StateContext";
import { Status } from "../../../../util/enums";
import Button, { Color } from "../../../Form/Button";
import CreateItemModal from "../modals/CreateItemModal";
import ListContent from "./ListContent";
import useStyles from "./styles";

interface InventoryListProps {
  jobId: number;
  rightIcon?: React.ReactChild;
  destination?: React.ReactChild;
  roomNameSelected?: string;
  addRoom: (room: RoomJob) => void;
  addItemIntoRoom: (item: Item) => void;
  addMaterialIntoRoom: (material: ServiceBooking) => void;
  externalList?: ExternalList;
}

enum Tab {
  ROOMS,
  ITEMS,
  MATERIALS,
}

const InventoryList = ({
  rightIcon,
  destination,
  roomNameSelected,
  addRoom,
  addItemIntoRoom,
  addMaterialIntoRoom,
  externalList,
}: InventoryListProps) => {
  const classes = useStyles();
  const { t: translate } = useTranslation();

  const [rooms, setRooms] = useState<RoomJob[]>(externalList?.roomList || []);
  const [itemsMaterials, setItemsMaterials] = useState<Array<Item | ServiceBooking>>(
    externalList?.itemList || []
  );
  const [materials, setMaterials] = useState<ServiceBooking[]>(externalList?.materialList || []);

  const [tabSelected, setTabSelected] = useState<Tab>(Tab.ROOMS);

  const [openModalItem, setOpenModalItem] = useState<boolean>(false);

  const { currentUser } = useAppState();

  useEffect(() => {
    checkMaterialsIsItem();
  }, [materials]);

  const { isFetching: isFetchingItem } = useQuery(
    ["items.getAll"],
    () =>
      ItemsService.getAll({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
        include: JSON.stringify([{ association: "rooms" }]),
        orderBy: JSON.stringify(["name"]),
      }),
    {
      enabled: !!currentUser?.companyId && !externalList?.itemList,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response.data && response.data.length > 0) {
          setItemsMaterials(response.data);
        }
        getMaterials();
      },
    }
  );

  const { isFetching: isFetchingRoom } = useQuery(
    ["room.getAllRoomJob"],
    () =>
      RoomService.getAllRoomJob({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
        orderBy: JSON.stringify(["name"]),
        jobId: "",
      }),
    {
      enabled: !!currentUser?.companyId && !externalList?.roomList,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response.data && response.data.length > 0) {
          setRooms(response.data);
        }
      },
    }
  );

  const { mutate: getMaterials, isLoading: isLoadingMaterials } = useMutation(
    ["materials.getAll"],
    () =>
      MaterialsService.getAll({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
        include: JSON.stringify([{ association: "rooms", attributes: ["id", "name", "code"] }]),
        orderBy: JSON.stringify(["name"]),
      }),
    {
      onSuccess: (response) => {
        if (response.data && response.data.length > 0) {
          setMaterials(response.data);
        }
      },
    }
  );

  const checkMaterialsIsItem = () => {
    const itemsToUpdate: Array<Item | ServiceBooking> = itemsMaterials;

    if (materials && materials?.length > 0) {
      materials.forEach((material: ServiceBooking) => {
        if (material?.isItem) {
          itemsToUpdate.push(material);
        }
      });
    }
    setItemsMaterials(
      itemsToUpdate.sort((a, b) => {
        if (a.name > b.name) return 1;
        if (a.name < b.name) return -1;

        return 0;
      })
    );
  };

  const renderRoomsPanel = () => {
    return (
      <div>
        <ListContent
          rightIcon={rightIcon}
          isLoading={isFetchingRoom}
          data={rooms}
          addIntoList={(parameter: RoomJob) => addRoom(parameter)}
          extractInfo={(item: RoomJob) => ({ name: item.name })}
        />
      </div>
    );
  };

  const renderItemsPanel = () => {
    return (
      <div data-cy="itemsListComponent">
        <ListContent
          rightIcon={rightIcon}
          data={itemsMaterials}
          renderChild={destination}
          addIntoList={(itemMaterial) => {
            // eslint-disable-next-line no-prototype-builtins
            if (!itemMaterial.hasOwnProperty("isItem")) {
              addItemIntoRoom(itemMaterial as Item);
            } else {
              addMaterialIntoRoom(itemMaterial as ServiceBooking);
            }
          }}
          isLoading={isFetchingItem}
          extractInfo={(itemMaterial) => ({
            name: itemMaterial.name,
            id: itemMaterial.id,
          })}
        />
        <div className={classes.containerButtonItemsPanel}>
          <Button
            onClick={() => setOpenModalItem(true)}
            customColor={Color.SECONDARY}
            startIcon={<AddCircleOutlineRoundedIcon />}
            className={classes.button}
          >
            {translate("addNewItem")}
          </Button>
        </div>
      </div>
    );
  };

  const getFilteredMaterials = () => {
    if (!roomNameSelected || !materials || materials.length === 0) return materials;

    const filtered = materials.filter((material) =>
      material?.rooms?.some((room) => room?.name === roomNameSelected)
    );

    return filtered.length > 0 ? filtered : materials;
  };

  const renderMaterialsPanel = () => {
    const materialsToRender = getFilteredMaterials();

    return (
      <ListContent
        rightIcon={rightIcon}
        isLoading={isLoadingMaterials}
        renderChild={destination}
        data={materialsToRender}
        addIntoList={(parameter: ServiceBooking) => addMaterialIntoRoom(parameter)}
        extractInfo={(item: ServiceBooking) => ({
          name: item.name ? item.name : "",
          id: item.id,
        })}
      />
    );
  };

  const renderContent = () => {
    switch (tabSelected) {
      case Tab.ITEMS:
        return renderItemsPanel();
      case Tab.MATERIALS:
        return renderMaterialsPanel();
      case Tab.ROOMS:
        return renderRoomsPanel();
      default:
        return renderRoomsPanel();
    }
  };

  const isActiveTab = (tab: Tab) => {
    return tabSelected === tab;
  };

  return (
    <div data-cy="inventoryTab" className={classes.InventoryListWrapper}>
      <ButtonGroup fullWidth>
        <Button
          data-cy="roomButton"
          onClick={() => setTabSelected(Tab.ROOMS)}
          customColor={isActiveTab(Tab.ROOMS) ? Color.SECONDARY : Color.NOT_SELECTED}
          className={classes.buttonGroup}
        >
          {translate("rooms")}
        </Button>
        <Button
          data-cy="itemButtonToList"
          onClick={() => setTabSelected(Tab.ITEMS)}
          customColor={isActiveTab(Tab.ITEMS) ? Color.SECONDARY : Color.NOT_SELECTED}
          className={classes.buttonGroup}
        >
          {translate("items")}
        </Button>
        <Button
          data-cy="materialButtonToList"
          onClick={() => setTabSelected(Tab.MATERIALS)}
          customColor={isActiveTab(Tab.MATERIALS) ? Color.SECONDARY : Color.NOT_SELECTED}
          className={classes.buttonGroup}
        >
          {translate("materials")}
        </Button>
      </ButtonGroup>
      {renderContent()}
      <CreateItemModal
        openModal={openModalItem}
        closeModal={() => setOpenModalItem(false)}
        onAddItem={(item: Item) => addItemIntoRoom(item)}
      />
    </div>
  );
};

export default InventoryList;
